import { createLogger } from '@ctrl-enter/tools/utils';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const logger = createLogger('web:store:widget-token');

/**
 * Token state with expiry tracking
 */
type TokenState = {
  token: string | null;
  expiresAt: number | null; // Unix timestamp in seconds
  isLoading: boolean;
  error: Error | null;
};

/**
 * Store actions for token management
 */
type TokenActions = {
  setToken: (token: string, expiresAt: number) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
  clearToken: () => void;
  reset: () => void;
};

/**
 * Combined store type
 */
type WidgetTokenStore = TokenState & TokenActions;

/**
 * Initial state
 */
const initialState: TokenState = {
  token: null,
  expiresAt: null,
  isLoading: false,
  error: null,
};

/**
 * Calculate token expiry time from JWT
 */
export const getTokenExpiry = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ?? null;
  } catch (error) {
    logger.error('❌ [WIDGET-TOKEN] Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired or expiring soon
 */
export const isTokenExpiringSoon = (
  expiresAt: number | null,
  refreshBeforeMinutes: number
): boolean => {
  if (!expiresAt) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  const refreshThreshold = expiresAt - refreshBeforeMinutes * 60;

  return now >= refreshThreshold;
};

/**
 * Centralized Zustand store for WorkOS widget token
 *
 * This ensures a single source of truth for the widget token across all components.
 * All WorkOS widgets (UserProfile, UsersManagement, etc.) share the same token.
 */
export const useWidgetTokenStore = create<WidgetTokenStore>()(
  devtools(
    set => ({
      ...initialState,

      setToken: (token, expiresAt) => {
        logger.info(
          '🎫 [WIDGET-TOKEN-STORE] Token updated:',
          JSON.stringify(
            {
              tokenLength: token.length,
              expiresAt,
              expiresAtDate: new Date(expiresAt * 1000).toISOString(),
            },
            null,
            2
          )
        );

        set({ token, expiresAt, isLoading: false, error: null });
      },

      setLoading: isLoading => set({ isLoading }),

      setError: error => {
        logger.error('❌ [WIDGET-TOKEN-STORE] Error:', error);
        set({ error, isLoading: false, token: null, expiresAt: null });
      },

      clearToken: () => {
        logger.info('🎫 [WIDGET-TOKEN-STORE] Token cleared');
        set({ token: null, expiresAt: null });
      },

      reset: () => {
        logger.info('🎫 [WIDGET-TOKEN-STORE] Store reset');
        set(initialState);
      },
    }),
    { name: 'widget-token-store' }
  )
);

// Atomic selectors for optimal re-render performance
export const useToken = () => useWidgetTokenStore(state => state.token);
export const useTokenExpiry = () => useWidgetTokenStore(state => state.expiresAt);
export const useTokenLoading = () => useWidgetTokenStore(state => state.isLoading);
export const useTokenError = () => useWidgetTokenStore(state => state.error);

// Action selectors
export const useSetToken = () => useWidgetTokenStore(state => state.setToken);
export const useSetLoading = () => useWidgetTokenStore(state => state.setLoading);
export const useSetError = () => useWidgetTokenStore(state => state.setError);
export const useClearToken = () => useWidgetTokenStore(state => state.clearToken);
export const useResetTokenStore = () => useWidgetTokenStore(state => state.reset);
